import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { CreditCard, Landmark, Lock, ShieldCheck, CheckCircle, XCircle, X, Loader } from 'lucide-react';
import './PaymentModal.css';

/**
 * Interface de paiement réelle.
 *
 * Props :
 *  - open, onClose
 *  - order  { code, document, price, currency }
 *  - submit (payload) → appel API : retourne { payUrl } (passerelle),
 *    { simulated } (formulaire carte) ou { payment } (virement/confirmé)
 *  - onPaid(payment) : callback après paiement réussi
 */

const luhn = (digits) => {
  let sum = 0;
  let dbl = false;
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let d = Number(digits[i]);
    if (dbl) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    dbl = !dbl;
  }
  return sum % 10 === 0;
};

const formatCardNumber = (v) =>
  v.replace(/\D/g, '').slice(0, 19).replace(/(\d{4})(?=\d)/g, '$1 ');

const formatExp = (v) => {
  const d = v.replace(/\D/g, '').slice(0, 4);
  return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
};

function validateCardForm(card) {
  const num = card.number.replace(/\D/g, '');
  const exp = card.exp;
  const cvc = card.cvc.replace(/\D/g, '');
  if (card.holder.trim().length < 2) return 'Titulaire de la carte requis.';
  if (!/^\d{13,19}$/.test(num) || !luhn(num)) return 'Numéro de carte invalide.';
  const m = exp.match(/^(\d{2})\/(\d{2})$/);
  if (!m || Number(m[1]) < 1 || Number(m[1]) > 12) return 'Date d’expiration invalide (MM/AA).';
  const year = 2000 + Number(m[2]);
  const now = new Date();
  if (year < now.getFullYear() || (year === now.getFullYear() && Number(m[1]) < now.getMonth() + 1)) {
    return 'Carte expirée.';
  }
  if (!/^\d{3,4}$/.test(cvc)) return 'Cryptogramme (CVC) invalide.';
  return null;
}

const FIELD_ERRORS = {
  cardHolder: 'Titulaire de la carte requis.',
  cardNumber: 'Numéro de carte invalide.',
  cardExp: 'Date d’expiration invalide ou carte expirée.',
  cardCvc: 'Cryptogramme (CVC) invalide.',
};

export default function PaymentModal({ open, onClose, order, submit, onPaid }) {
  const [step, setStep] = useState('method'); // method | card | processing | success | failed | transfer
  const [method, setMethod] = useState('card');
  const [card, setCard] = useState({ holder: '', number: '', exp: '', cvc: '' });
  const [error, setError] = useState('');
  const [payment, setPayment] = useState(null);
  const [bank, setBank] = useState(null);

  if (!open || !order) return null;

  const amount = `${order.price} ${order.currency || 'TND'}`;
  const orderCode = order.code || order.id;
  const close = () => {
    setStep('method');
    setError('');
    setPayment(null);
    setBank(null);
    onClose();
  };

  const apiError = (err) => {
    if (err?.data?.field && FIELD_ERRORS[err.data.field]) return FIELD_ERRORS[err.data.field];
    if (err?.data?.error === 'translatorNotConfirmed') {
      return 'Paiement disponible dès qu’un professionnel aura accepté votre commande.';
    }
    if (err?.data?.error === 'gatewayUnavailable') {
      return 'La passerelle de paiement est momentanément indisponible. Réessayez plus tard.';
    }
    if (err?.data?.error === 'alreadyPaid') return 'Cette commande est déjà réglée.';
    return 'Erreur lors du paiement. Veuillez réessayer.';
  };

  // Méthode choisie → phase 1 : passerelle, virement ou formulaire carte
  const startPayment = async () => {
    setError('');
    setStep('processing');
    try {
      const res = await submit({ method });
      if (res.payUrl) {
        window.location.href = res.payUrl;
        return;
      }
      if (res.simulated) {
        setStep('card');
        return;
      }
      if (res.pending) {
        setPayment(res.payment || null);
        setBank(res.bank || null);
        setStep('transfer');
        return;
      }
      if (res.payment?.status === 'paid') {
        setPayment(res.payment);
        setStep('success');
        onPaid?.(res.payment);
        return;
      }
      setStep('method');
    } catch (err) {
      setError(apiError(err));
      setStep('method');
    }
  };

  // Phase 2 — envoi de la carte (mode passerelle locale)
  const payWithCard = async (e) => {
    e.preventDefault();
    const err = validateCardForm(card);
    if (err) {
      setError(err);
      return;
    }
    setError('');
    setStep('processing');
    try {
      const res = await submit({ method: 'card', card });
      if (res.payment?.status === 'paid') {
        setPayment(res.payment);
        setStep('success');
        onPaid?.(res.payment);
      } else {
        setStep('failed');
      }
    } catch (err2) {
      const msg = apiError(err2);
      if (FIELD_ERRORS[err2?.data?.field]) {
        setError(msg);
        setStep('card');
      } else {
        setError(msg);
        setStep('failed');
      }
    }
  };

  const digits = card.number.replace(/\D/g, '');

  // Portal sur body : sinon un ancêtre avec transform/backdrop-filter
  // casse le position:fixed et la modale apparaît en bas de page.
  return createPortal(
    <div className="pay-overlay" role="dialog" aria-modal="true" aria-label="Paiement sécurisé">
      <div className="pay-modal">
        <button type="button" className="pay-close" onClick={close} aria-label="Fermer">
          <X size={18} />
        </button>

        <header className="pay-head">
          <span className="pay-head-icon"><Lock size={16} /></span>
          <div>
            <h2>Paiement sécurisé</h2>
            <p>{orderCode} · {order.document}</p>
          </div>
          <strong className="pay-amount">{amount}</strong>
        </header>

        {step === 'method' && (
          <div className="pay-body">
            <p className="pay-label">Choisissez votre moyen de paiement</p>
            <div className="pay-methods">
              <button
                type="button"
                className={`pay-method${method === 'card' ? ' is-active' : ''}`}
                onClick={() => setMethod('card')}
              >
                <CreditCard size={20} />
                <span>
                  <strong>Carte bancaire</strong>
                  <small>Visa · Mastercard · e-DINAR</small>
                </span>
              </button>
              <button
                type="button"
                className={`pay-method${method === 'transfer' ? ' is-active' : ''}`}
                onClick={() => setMethod('transfer')}
              >
                <Landmark size={20} />
                <span>
                  <strong>Virement bancaire</strong>
                  <small>Confirmation sous 24–48h</small>
                </span>
              </button>
            </div>
            {error && <p className="pay-error" role="alert">{error}</p>}
            <button type="button" className="btn btn-primary pay-cta" onClick={startPayment}>
              <ShieldCheck size={16} /> Payer {amount}
            </button>
            <p className="pay-secure-note">
              <Lock size={12} /> Transaction chiffrée — vos données bancaires ne sont jamais stockées.
            </p>
          </div>
        )}

        {step === 'card' && (
          <form className="pay-body" onSubmit={payWithCard}>
            <div className="pay-card-preview" aria-hidden="true">
              <span className="pay-card-chip" />
              <span className="pay-card-num">{card.number || '•••• •••• •••• ••••'}</span>
              <span className="pay-card-row">
                <em>{card.holder || 'TITULAIRE'}</em>
                <em>{card.exp || 'MM/AA'}</em>
              </span>
            </div>
            <label className="pay-field">
              <span>Titulaire de la carte</span>
              <input
                value={card.holder}
                onChange={(e) => setCard({ ...card, holder: e.target.value.toUpperCase() })}
                placeholder="NOM PRÉNOM"
                autoComplete="cc-name"
              />
            </label>
            <label className="pay-field">
              <span>Numéro de carte</span>
              <input
                value={card.number}
                onChange={(e) => setCard({ ...card, number: formatCardNumber(e.target.value) })}
                placeholder="1234 5678 9012 3456"
                inputMode="numeric"
                autoComplete="cc-number"
              />
            </label>
            <div className="pay-grid2">
              <label className="pay-field">
                <span>Expiration</span>
                <input
                  value={card.exp}
                  onChange={(e) => setCard({ ...card, exp: formatExp(e.target.value) })}
                  placeholder="MM/AA"
                  inputMode="numeric"
                  autoComplete="cc-exp"
                />
              </label>
              <label className="pay-field">
                <span>CVC</span>
                <input
                  value={card.cvc}
                  onChange={(e) => setCard({ ...card, cvc: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                  placeholder="123"
                  inputMode="numeric"
                  autoComplete="cc-csc"
                />
              </label>
            </div>
            {error && <p className="pay-error" role="alert">{error}</p>}
            <button type="submit" className="btn btn-primary pay-cta" disabled={!digits.length}>
              <Lock size={16} /> Payer {amount}
            </button>
          </form>
        )}

        {step === 'processing' && (
          <div className="pay-body pay-center">
            <Loader size={34} className="pay-spin" />
            <p>Traitement du paiement en cours…</p>
            <small>Ne fermez pas cette fenêtre.</small>
          </div>
        )}

        {step === 'success' && (
          <div className="pay-body pay-center">
            <CheckCircle size={42} className="pay-ok" />
            <h3>Paiement confirmé</h3>
            <p>{amount} — réf. {payment?.id || payment?.code || ''}</p>
            <button type="button" className="btn btn-primary" onClick={close}>Terminer</button>
          </div>
        )}

        {step === 'failed' && (
          <div className="pay-body pay-center">
            <XCircle size={42} className="pay-ko" />
            <h3>Paiement refusé</h3>
            <p>{error || 'La transaction a été refusée. Vérifiez votre carte ou essayez un autre moyen de paiement.'}</p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className="btn btn-outline" onClick={close}>Annuler</button>
              <button type="button" className="btn btn-primary" onClick={() => { setError(''); setStep('method'); }}>
                Réessayer
              </button>
            </div>
          </div>
        )}

        {step === 'transfer' && (
          <div className="pay-body">
            <div className="pay-transfer">
              <Landmark size={28} />
              <h3>Instructions de virement</h3>
              <p>Votre commande <strong>{orderCode}</strong> sera confirmée dès réception du virement.</p>
              <dl>
                <div><dt>Bénéficiaire</dt><dd>{bank?.beneficiary || 'Tarjemlek'}</dd></div>
                <div><dt>Banque</dt><dd>{bank?.name || 'Amen Bank — Agence Centre Urbain Nord'}</dd></div>
                <div><dt>RIB</dt><dd>{bank?.rib || '07 005 0123456789012 34'}</dd></div>
                {bank?.swift ? <div><dt>SWIFT / BIC</dt><dd>{bank.swift}</dd></div> : null}
                <div><dt>Montant</dt><dd><strong>{amount}</strong></dd></div>
                <div><dt>Référence (motif)</dt><dd><strong>{payment?.reference || `TAR-${orderCode}`}</strong></dd></div>
              </dl>
              <p style={{ fontSize: '0.82rem', color: 'var(--gray-600, #475569)' }}>
                Indiquez cette référence dans le motif du virement pour faciliter l’identification du paiement.
              </p>
            </div>
            <button type="button" className="btn btn-primary pay-cta" onClick={close}>Compris</button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
